import { extend } from 'vee-validate';
import { alpha_num, max, required } from 'vee-validate/dist/rules';
import { isAfter, parse, toDate } from "date-fns";

/**
 * Extended functions for vee-validate in one place.
 * Imported in main.js since previous iterations of including the 'extend' in each component's
 * global space was redundant.
 */

export default {
  alphaNum() {
    extend('alpha_num', {
      ...alpha_num,
      message: 'Only alphanumeric characters are allowed.'
    });
  },

  fieldRequired() {
    extend('fieldRequired', {
      ...required,
      message: '{_field_} is required.'
    });
  },

  minDate() {
    extend('minDate', {
      validate: (value) => {
        // plain text input field intervention
        if ((value.match(/\d{2}\/\d{2}\/\d{4}$/g) || []).length === 1) {
          const tmp = value;
          value = `${tmp.substr(6, 4)}-${tmp.substr(0, 2)}-${tmp.substr(3, 2)}`;
        }

        const minDate = toDate(new Date(1751, 11, 31, 23, 59, 59));
        const curDate = parse(value, 'yyyy-MM-dd', new Date());

        return isAfter(curDate, minDate);
      },
      message: 'Date must be within the acceptable range.'
    });
  },

  minMax() {
    extend('minMax', {
      validate(value, args) {
        const length = value.length;
        return length >= args.min && length <= args.max;
      },
      params: [ 'min', 'max' ],
      message: (field, params) => {
        return `${ params._field_ } must have ${ params.min } or ${ params.max } characters.`;
      }
    });
  },

  max() {
    extend('max', {
      ...max,
      message: (field, params) => {
        return `${ params._field_ } is limited to ${ params.length } characters.`;
      }
    });
  },

  monthBetween() {
    extend('monthBetween', {
      validate(value, args) {
        return (args.min <= Number(value) && args.max >= Number(value)) && (value.length === 2);
      },
      params: [ 'min', 'max' ],
      message: (field, params) => {
        return `${ params._field_ } must be between 01 and 12.`;
      }
    });
  },

  specialChars() {
    extend('specialChars', {
      validate: (value) => {
        const pattern = /^[a-zA-Z0-9!@#$%^&*()_+=[\]{}\u2018\u2019':"\\.>/?`~ ]*$/;
        return pattern.test(value);
      },
      message: 'Special characters ";", "<", "|", "," and "-" are not allowed. ' +
        'For hyphenated names please use a space in place of hyphen / dash.'
    });
  },

  validYear() {
    extend('validYear', {
      // Business rule ~ "Valid values for YYYY part of the date: Current year plus 20"
      validate(value, args) {
        const currentYr = new Date().getFullYear();
        return (currentYr <= Number(value)) && ((currentYr + args.plus) >= Number(value));
      },
      params: [ 'plus' ],
      message: (field, params) => {
        return `${ params._field_ } is not valid.`;
      }
    });
  },
}
